/**
 * MTGA / Arena format exporters
 *
 * Collection: MTGA text format (set codes + collector numbers).
 * Deck: Arena format with About/Name preamble and zone headers.
 * Split/adventure/DFC cards use front face name only in deck export.
 */
import type { EnrichedCollectionCard } from '@/types/card';
import type { DeckZone } from '@/types/deck';

import type { ExportableDeckCard } from '../../decks/lib/deckImportExport';

// ============================================================================
// Collection Export
// ============================================================================

/**
 * Export collection to MTGA text format.
 * Format: 4 Lightning Bolt (M10) 146
 */
export function exportMtga(cards: EnrichedCollectionCard[]): string {
  const lines = cards.map((item) => {
    const quantity = item.quantity;
    const name = item.card.name;
    const setCode = item.card.set.toUpperCase();
    const collectorNumber = item.card.collector_number;

    return `${quantity} ${name} (${setCode}) ${collectorNumber}`;
  });

  return lines.join('\n');
}

// ============================================================================
// Deck Export
// ============================================================================

export interface ArenaExportOptions {
  includeMaybeboard?: boolean;
}

/**
 * Export a deck in Arena format.
 *
 * Format: About/Name preamble, Commander/Deck zone headers, no set codes.
 * Split/adventure/DFC cards use front face name only.
 *
 * Example:
 * ```
 * About
 * Name Temur Roar
 *
 * Commander
 * 1 Ureni of the Unwritten
 *
 * Deck
 * 1 Arcane Signet
 * ...
 * ```
 */
export function exportDeckListArena(cards: ExportableDeckCard[], deckName?: string, options: ArenaExportOptions = {}): string {
  const { includeMaybeboard = false } = options;
  const lines: string[] = [];

  // Preamble
  lines.push('About');
  lines.push(`Name ${deckName ?? 'Untitled Deck'}`);

  // Arena card line: quantity + front face name, no set codes
  const formatArenaLine = (item: ExportableDeckCard): string => {
    return `${item.quantity} ${getFrontFaceName(item.card.name)}`;
  };

  // Zone order: commander, companion, main deck, sideboard
  const zoneConfig: { zone: DeckZone; header: string }[] = [
    { zone: 'commander', header: 'Commander' },
    { zone: 'companion', header: 'Companion' },
    { zone: 'main', header: 'Deck' },
    { zone: 'sideboard', header: 'Sideboard' },
  ];
  if (includeMaybeboard) {
    zoneConfig.push({ zone: 'maybeboard', header: 'Sideboard' });
  }

  for (const { zone, header } of zoneConfig) {
    // Skip maybeboard if already merged into sideboard above
    if (zone === 'maybeboard' && !includeMaybeboard) continue;

    const zoneCards = cards.filter((c) => c.zone === zone).sort((a, b) => a.card.name.localeCompare(b.card.name));

    if (zoneCards.length === 0) continue;

    // Don't duplicate sideboard header if maybeboard already printed it
    lines.push('');
    lines.push(header);

    for (const item of zoneCards) {
      lines.push(formatArenaLine(item));
    }
  }

  return lines.join('\n').trim();
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Get the front face name of a card (for Arena export).
 * Splits on " // " (Scryfall standard) or " / " (some formats).
 */
function getFrontFaceName(name: string): string {
  const match = name.match(/^(.+?)\s+\/\/?\s+/);
  return match ? match[1] : name;
}
